import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from '@/plugins/axios';
import { fetchFoldersCached, invalidateFolders } from '@/composables/useExperimentQuery';
import { useUserStore } from './user';
import { BRANDING } from '@/branding'; // Import branding constants
import { isExperimentFullyReady } from '@/utils/experimentReadiness';

export const useDataStore = defineStore('data', () => {
    const folderTree = ref([]);
    const allTags = ref([]);
    const isLoadingFolders = ref(false);
    const isLoadingTags = ref(false);
    // True until the first refreshAllData() resolves. Prevents the "no projects"
    // empty state from flashing during the gap between component mount and the
    // first fetch starting (when isLoadingFolders/Tags are still false).
    const hasInitialLoad = ref(false);
    // SYN-2738 U7 — true when /folders failed (non-401) and there is no tree of
    // THIS client to keep: the store falls back to the intro structure, and the
    // dashboard must show an error + retry instead of an empty/intro workspace.
    const folderLoadError = ref(false);
    const _staticIntroStructure = ref([
        {
            id: 'intro-root', name: 'Central de Ajuda', type: 'static-folder',
            children: [
                { id: 'intro-primeiros-passos', name: 'Primeiros Passos', type: 'static-page', bookmark: '#primeiros-passos' },
                { id: 'intro-analise', name: 'Análise de Criativos', type: 'static-page', bookmark: '#analise-criativos' },
                { id: 'intro-aoi', name: 'Áreas de Interesse', type: 'static-page', bookmark: '#areas-interesse' },
                { id: 'intro-neurogen', name: 'NeuroGen AI', type: 'static-page', bookmark: '#neurogen-ai' },
                { id: 'intro-exportacao', name: 'Exportação', type: 'static-page', bookmark: '#exportacao' },
                { id: 'intro-organizacao', name: 'Organização', type: 'static-page', bookmark: '#organizacao' },
                { id: 'intro-creditos', name: 'Créditos e Uso', type: 'static-page', bookmark: '#creditos' },
                { id: 'intro-atalhos', name: 'Atalhos e Dicas', type: 'static-page', bookmark: '#atalhos' },
                { id: 'intro-sobre', name: 'Sobre o Serviço', type: 'static-page', bookmark: '#sobre-servico' },
                { id: 'intro-metodologia', name: 'Metodologia', type: 'static-page', bookmark: '#metodologia' },
                { id: 'intro-como-funciona', name: 'Como Funciona a IA', type: 'static-page', bookmark: '#como-funciona-ia' },
                { id: 'intro-neural', name: 'Neural Engagement', type: 'static-page', bookmark: '#neural-engagement' },
            ],
            expanded: false,
        }
    ]);

    const isLoading = computed(() => !hasInitialLoad.value || isLoadingFolders.value || isLoadingTags.value);
    const userStore = useUserStore();

    const clientRootFolder = computed(() => {
        const clientId = userStore.effectiveClientId; // Use effective ID
        if (!folderTree.value || folderTree.value.length === 0 || !clientId) return null;
        const userContent = folderTree.value.filter(item => !item.id?.startsWith('intro-'));
        return userContent.find(item => item.type === 'folder' && item.parent_folder_id === null && item.client_id === clientId);
    });

    function getExpandedFolderIds(items) {
        const expandedIds = new Set();
        const traverse = (nodes) => {
            if (!nodes || nodes.length === 0) return;
            for (const node of nodes) {
                if ((node.type === 'folder' || node.type === 'static-folder') && node.expanded) {
                    expandedIds.add(node.id);
                    if (node.children) { traverse(node.children); }
                }
            }
        };
        traverse(items);
        return expandedIds;
    };

    // Dono da árvore que está em memória. Existe por causa da regra do catch abaixo:
    // manter a árvore num erro transitório é certo para o MESMO cliente e é
    // vazamento entre clientes quando o dono mudou. Se o primeiro /folders do
    // cliente impersonado falha, sem este carimbo a sidebar continua mostrando as
    // pastas do cliente anterior. `clearScopedCaches()` (stores/user.js) não cobre:
    // ele limpa o queryClient e os caches de mídia, nunca o folderTree.
    let _treeClientId = null;

    function buildItemTree(items, expandedIds) {
        const itemMap = new Map();
        const clientId = userStore.effectiveClientId; // Use effective ID
        if (!clientId) {
            console.error("DataStore: Client ID missing, cannot build tree.");
            _treeClientId = null;
            folderTree.value = JSON.parse(JSON.stringify(_staticIntroStructure.value));
            return;
        }

        // SYN-1015: snapshot dos experimentos da árvore ATUAL (antes de
        // substituí-la) para preservar o latch de readiness. Se um criativo já
        // foi observado 100% pronto, o rebuild não pode devolvê-lo para "Em
        // andamento" por causa de um /folders defasado (lag de réplica etc.).
        const previousExperiments = new Map();
        const collectExperiments = (nodes) => {
            for (const node of nodes) {
                if (node.type === 'experiment') previousExperiments.set(node.id, node);
                else if (node.children) collectExperiments(node.children);
            }
        };
        collectExperiments(folderTree.value);
        const makeExperimentNode = (exp) => {
            const node = { ...exp, type: 'experiment' };
            const prev = previousExperiments.get(exp.id);
            // Só carimba o latch se o item anterior estava genuinamente pronto.
            // isExperimentFullyReady já propaga _readyLatched do nó anterior, então
            // o latch sobrevive a rebuilds sucessivos (e se limpa numa falha).
            if (prev && isExperimentFullyReady(prev)) node._readyLatched = true;
            return node;
        };

        const clientFolders = items.filter(item => item.client_id === clientId && item.type === 'folder');
        const clientExperiments = items.filter(item => item.client_id === clientId && item.type === 'experiment');

        clientFolders.forEach(folder => {
            itemMap.set(folder.id, {
                ...folder,
                children: [],
                expanded: expandedIds.has(folder.id),
                name: folder.parent_folder_id === null ? BRANDING.LABEL_ROOT_FOLDER : folder.name,
            });
        });

        const userRoots = [];
        itemMap.forEach((folderNode) => {
            if (folderNode.parent_folder_id) {
                const parentNode = itemMap.get(folderNode.parent_folder_id);
                if (parentNode && parentNode.type === 'folder') {
                    parentNode.children?.push(folderNode);
                } else {
                    const clientRootFallback = [...itemMap.values()].find(i => i.client_id === folderNode.client_id && !i.parent_folder_id && i.type === 'folder');
                    if (clientRootFallback) { clientRootFallback.children?.push(folderNode); folderNode.parent_folder_id = clientRootFallback.id;}
                    else { console.error("Cannot find parent for orphan folder:", folderNode); }
                }
            } else if (folderNode.type === 'folder') {
                userRoots.push(folderNode);
                // Force expansion if it's the root folder
                if (folderNode.parent_folder_id === null) {
                    folderNode.expanded = true;
                    expandedIds.add(folderNode.id);
                } else if (expandedIds.has(folderNode.id)) {
                    folderNode.expanded = true;
                }
            }
        });

        clientExperiments.forEach(exp => {
            const parentFolderNode = itemMap.get(exp.folder_id);
            if (parentFolderNode && parentFolderNode.type === 'folder') {
                parentFolderNode.children?.push(makeExperimentNode(exp));
            } else {
                console.warn(`Experiment ${exp.id} has parent folder ${exp.folder_id} not found or not a folder. Placing under root.`);
                const clientRoot = userRoots.find(r => r.id === userStore.clientRootFolder?.id);
                if (clientRoot) {
                    clientRoot.children?.push(makeExperimentNode(exp));
                } else {
                     console.error("Could not find a root folder to place orphaned experiment:", exp);
                }
            }
        });

        itemMap.forEach(folderNode => {
            if (folderNode.children) {
                folderNode.children.sort((a, b) => {
                    if (a.type === 'folder' && b.type !== 'folder') return -1;
                    if (a.type !== 'folder' && b.type === 'folder') return 1;
                    return (a.name || '').localeCompare(b.name || '', 'pt-BR');
                });
            }
        });
        userRoots.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        const staticTree = JSON.parse(JSON.stringify(_staticIntroStructure.value));
        if (expandedIds.has(staticTree[0].id)) {
            staticTree[0].expanded = true;
        }
        _treeClientId = clientId;
        folderTree.value = [...staticTree, ...userRoots];
    }


    function isAbortError(error) {
        return error?.name === 'CanceledError' || error?.name === 'AbortError' || axios.isCancel?.(error);
    }

    async function fetchFolderContents({ signal, force = false } = {}) {
        if (!userStore.isAuthenticated) {
            _treeClientId = null;
            folderTree.value = JSON.parse(JSON.stringify(_staticIntroStructure.value));
            isLoadingFolders.value = false;
            return;
        }
        const expandedIds = getExpandedFolderIds(folderTree.value);
        isLoadingFolders.value = true;
        try {
            if (force) await invalidateFolders();
            const data = await fetchFoldersCached();
            if (signal?.aborted) return;
            /* istanbul ignore next -- mid-fetch auth race: user logged out after fetch resolved; untestable synchronously */
            if (!userStore.isAuthenticated) {
                folderTree.value = JSON.parse(JSON.stringify(_staticIntroStructure.value));
                return;
            }
            buildItemTree(data, expandedIds);
            folderLoadError.value = false;
        } catch (error) {
            if (isAbortError(error)) return;
            // UM REFRESH QUE FALHA NÃO PODE JOGAR A ÁRVORE FORA (2026-09-16).
            //
            // Este fetch roda a cada rajada de WebSocket (scheduleWsRefresh, a cada
            // ~5s quando há peças processando). Substituir a árvore do cliente pela
            // estática da intro num erro TRANSITÓRIO (rede, 429, token renovando)
            // tinha um efeito visível e desorientador: a reconciliação do Main não
            // encontra mais a pasta selecionada na árvore trocada e faz
            // replace('/main') — o usuário está DENTRO de uma pasta e é arremessado
            // para "Meus projetos" sem nenhuma ação dele. Reproduzido durante a
            // bateria de 126 peças, que emite status_update contínuo.
            //
            // A árvore em memória continua sendo o melhor dado disponível: fica.
            // O fallback estático só cabe quando NUNCA houve carga (primeiro fetch
            // falhou — sem ele a tela ficaria vazia) e no 401, onde a sessão morreu
            // e o dado do cliente não deve permanecer em memória.
            if (error.response?.status === 401) {
                _treeClientId = null;
                folderTree.value = JSON.parse(JSON.stringify(_staticIntroStructure.value));
                return;
            }
            console.error('Error fetching folder contents:', error);
            // Ficar com a árvore em memória só vale enquanto ela for DESTE cliente.
            // Trocar de cliente (impersonar, ou sair da impersonação) e ver o
            // primeiro /folders falhar deixaria as pastas do cliente anterior na
            // tela, com nome e tudo, até algum refresh dar certo.
            if (!hasInitialLoad.value || _treeClientId !== userStore.effectiveClientId) {
                _treeClientId = null;
                folderTree.value = JSON.parse(JSON.stringify(_staticIntroStructure.value));
                folderLoadError.value = true;
            }
        } finally {
            if (!signal?.aborted && userStore.isAuthenticated) isLoadingFolders.value = false;
        }
    }

    async function fetchAllTags() {
        if (!userStore.isAuthenticated) {
            allTags.value = []; isLoadingTags.value = false; return;
        }
        isLoadingTags.value = true;
        try {
            const response = await axios.get('/tags');
            /* istanbul ignore next -- mid-fetch auth race: user logged out while tags were loading */
            if (!userStore.isAuthenticated) { allTags.value = []; return; }
            allTags.value = response.data;
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error("Error fetching all tags:", error); allTags.value = [];
            } else { allTags.value = []; return; }
        } finally {
            if (userStore.isAuthenticated) isLoadingTags.value = false;
        }
    }

    async function refreshAllData({ signal, force = false } = {}) {
        if (!userStore.isAuthenticated) {
            folderTree.value = JSON.parse(JSON.stringify(_staticIntroStructure.value));
            allTags.value = []; isLoadingFolders.value = false; isLoadingTags.value = false;
            hasInitialLoad.value = true;
            return;
        }
        try {
            await Promise.all([fetchFolderContents({ signal, force }), fetchAllTags()]);
        } finally {
            if (!signal?.aborted) hasInitialLoad.value = true;
        }
    }

    // Silent refresh: fetches data without triggering loading/skeleton states
    async function refreshAllDataSilent() {
        if (!userStore.isAuthenticated) return;
        try {
            await invalidateFolders();
            const [data, tagsRes] = await Promise.all([
                fetchFoldersCached(),
                axios.get('/tags'),
            ]);
            /* istanbul ignore next -- mid-fetch auth race in silent refresh */
            if (!userStore.isAuthenticated) return;
            const expandedIds = getExpandedFolderIds(folderTree.value);
            buildItemTree(data, expandedIds);
            folderLoadError.value = false;
            allTags.value = tagsRes.data;
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error('Error in silent refresh:', error);
            }
        }
    }

    function findItemById(itemId, items = folderTree.value) {
        if (!items || items.length === 0 || !itemId) return null;
        for (const item of items) {
            if (item.id === itemId && (item.type === 'folder' || item.type === 'experiment' || item.id.startsWith('intro-'))) {
                return item;
            }
            if ((item.type === 'folder' || item.type === 'static-folder') && item.children && item.children.length > 0) {
                const found = findItemById(itemId, item.children);
                if (found) return found;
            }
        }
        return null;
    };

    function findParentOfItemById(itemId, items = folderTree.value, parent = null) {
        if (!items || items.length === 0 || !itemId) return null;
        for (const item of items) {
            if (item.id === itemId) { return parent; }
            if ((item.type === 'folder' || item.type === 'static-folder') && item.children && item.children.length > 0) {
                const foundParent = findParentOfItemById(itemId, item.children, item);
                if (foundParent) return foundParent;
            }
        }
        return null;
    };

    // --- Optimistic UI Actions ---
    function addOptimisticExperiment(experimentData, parentFolderId) {
        const parentFolder = findItemById(parentFolderId);
        if (parentFolder && parentFolder.type === 'folder') {
            if (!parentFolder.children) {
                parentFolder.children = [];
            }
            parentFolder.children.unshift(experimentData);
        }
    }

    function updateOptimisticExperiment(optimisticId, realExperimentData) {
        const optimisticItem = findItemById(optimisticId);
        if (optimisticItem) {
            // Preserve the local thumbnail URL
            const localThumbnail = optimisticItem.local_thumbnail_url;
            // Update the existing object in place with data from the server
            Object.assign(optimisticItem, realExperimentData);
            // Ensure the local thumbnail is not lost
            optimisticItem.local_thumbnail_url = localThumbnail;
            // Object.assign only adds/overwrites keys present in the source, so it
            // can't clear `isOptimistic` (absent from realExperimentData). Drop it
            // explicitly — the upload is done once the real id/status land.
            delete optimisticItem.isOptimistic;
        } else {
            console.warn(`[DataStore] Could not find optimistic experiment ${optimisticId} to update.`);
        }
    }

    function updateOptimisticExperimentStatus(optimisticId, newStatus) {
        const item = findItemById(optimisticId);
        if (item) {
            item.analysis_status = newStatus;
        }
    }

    function removeOptimisticExperiment(experimentId) {
        const parentFolder = findParentOfItemById(experimentId);
        if (parentFolder && parentFolder.children) {
            const index = parentFolder.children.findIndex(exp => exp.id === experimentId);
            /* istanbul ignore else -- index === -1 requires tree inconsistency; untestable without artificial data corruption */
            if (index !== -1) {
                parentFolder.children.splice(index, 1);
            }
        }
    }

    // --- Tree Expansion Logic ---
    function expandPathToId(itemId) {
        // Find the item first
        let current = findItemById(itemId);
        if (!current) return;

        // Expand the item itself if it's a folder
        if (current.type === 'folder' || current.type === 'static-folder') {
            current.expanded = true;
        }

        // Walk up the tree expanding parents
        let parent = findParentOfItemById(current.id);
        while (parent) {
            parent.expanded = true;
            parent = findParentOfItemById(parent.id);
        }
    }


    return {
        folderTree, allTags, isLoadingFolders, isLoadingTags, hasInitialLoad, folderLoadError,
        isLoading, clientRootFolder,
        fetchFolderContents, fetchAllTags, refreshAllData, refreshAllDataSilent,
        findItemById, findParentOfItemById, getExpandedFolderIds,
        addOptimisticExperiment, updateOptimisticExperiment, updateOptimisticExperimentStatus, removeOptimisticExperiment,
        expandPathToId
    };
});
